///
/// EarthBrowser
///
/// Created by Matt Giger on 3/4/2025
/// Copyright (c) 2025 Geodata Labs
///

import { type LayerLoadState } from "@/earthbrowser/datasets/LoadState"
import { type TextureFrame, TextureSeries } from "@/earthbrowser/datasets/TextureSeries"
import { type TimeLaneDetail } from "@/earthbrowser/datasets/TimeLane"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type timerange } from "@stovokor/math/time"
import { type Material } from "@stovokor/shader/Material"
import { type EarthBrowserContext } from "../EarthBrowserContext"

export class TextureOverlay {
	protected textureSeries: TextureSeries<TextureFrame>

	constructor(context: EarthBrowserContext) {
		this.textureSeries = new TextureSeries(context)
	}

	public setFrames(frames: TextureFrame[]): void {
		this.textureSeries.setFrames(frames)
	}

	/** Time span covered by this overlay's frames (undefined when empty) */
	public getTimeRange(): timerange | undefined {
		return this.textureSeries.timeRange
	}

	/** Frame start times and GPU-resident spans, for the timeline's lanes and frame stepping */
	public getTimeLaneDetail(): TimeLaneDetail {
		return {
			frameTimesMs: this.textureSeries.frameTimesMs(),
			loadedRanges: this.textureSeries.loadedRangesMs(),
			displayedFrameMs: this.textureSeries.displayedFrameTimeMs(),
		}
	}

	/** The frame the clock is waiting on, if the one on screen isn't it (see TextureSeries.loadState) */
	public getLoadState(): LayerLoadState | null {
		return this.textureSeries.loadState()
	}

	/**
	 * Free all GPU textures held by this overlay's series while keeping the
	 * frame metadata. Called when the owning layer is hidden so disabling a
	 * dataset actually reclaims memory; frames reload when re-shown.
	 */
	public purge(): void {
		this.textureSeries.purgeAll()
	}

	public frameUpdate(state: RenderState): void {
		this.textureSeries.updateState(state)
	}

	public overlayMaterial(): Material {
		throw new Error("Not implemented")
	}
}
